import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {useHome} from "../../models/home/use-home";
import {useRealtime} from "../realtime/useRealtime";

const FocusSessionContext = createContext(null);

export function entityIdentity(value) {
    return value?.documentId ?? value?.id ?? value ?? null;
}

export function sameEntity(left, right) {
    const leftKeys = [left?.documentId, left?.id, typeof left !== "object" ? left : null].filter((value) => value !== null && value !== undefined).map(String);
    const rightKeys = [right?.documentId, right?.id, typeof right !== "object" ? right : null].filter((value) => value !== null && value !== undefined).map(String);
    return leftKeys.some((value) => rightKeys.includes(value));
}

export function memberFocusStatus(memberData, presences) {
    const membership = memberData?.member ? memberData : null;
    const member = membership?.member || memberData;
    const memberKeys = [member?.documentId, member?.id].filter(Boolean).map(String);
    const membershipKeys = membership
        ? [membership.documentId, membership.id].filter(Boolean).map(String)
        : [];
    const matchedStatuses = (Array.isArray(presences) ? presences : []).flatMap((presence) => {
        if (!["running", "paused"].includes(presence?.focusSessionStatus)) return [];
        const focusedMembership = presence?.focusedBy;
        const focusedMember = focusedMembership?.member || focusedMembership;
        const focusedMemberKeys = [focusedMember?.documentId, focusedMember?.id].filter(Boolean).map(String);
        const focusedMembershipKeys = focusedMembership?.member
            ? [focusedMembership.documentId, focusedMembership.id].filter(Boolean).map(String)
            : [];
        const matches = memberKeys.some((key) => focusedMemberKeys.includes(key))
            || membershipKeys.some((key) => focusedMembershipKeys.includes(key));
        return matches ? [presence.focusSessionStatus] : [];
    });
    if (matchedStatuses.includes("running")) return "running";
    if (matchedStatuses.includes("paused")) return "paused";
    return null;
}

export function memberHasRunningFocus(memberData, presences) {
    return memberFocusStatus(memberData, presences) === "running";
}

export function focusSessionFromRealtimeEvent(event) {
    return event?.data?.session ?? event?.payload?.session ?? event?.session ?? null;
}

export function focusSessionBelongsToMembership(session, membership) {
    const focusedMembership = session?.focusedBy ?? session?.task?.assignee;
    return Boolean(focusedMembership && membership && sameEntity(focusedMembership, membership));
}

export function mergeFocusSessionCollection(currentItems, nextSession, include = () => true) {
    const items = Array.isArray(currentItems) ? currentItems : [];
    const key = entityIdentity(nextSession);
    if (key === null || key === undefined) return items;

    const currentIndex = items.findIndex((item) => sameEntity(item, nextSession));
    const isActive = ["running", "paused"].includes(nextSession?.focusSessionStatus) && include(nextSession);
    if (!isActive) {
        return currentIndex === -1 ? items : items.filter((_, index) => index !== currentIndex);
    }

    if (currentIndex === -1) return [...items, nextSession];
    const nextItems = [...items];
    nextItems[currentIndex] = {...items[currentIndex], ...nextSession};
    return nextItems;
}

export function FocusSessionProvider({children}) {
    const home = useHome();
    const realtime = useRealtime();
    const membership = useSelector((state) => state.app.workspaceMembershipData);
    const workspaceId = entityIdentity(membership?.workspace);
    const [session, setSession] = useState(null);
    const [activeSessions, setActiveSessions] = useState([]);
    const [focusPresences, setFocusPresences] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pending, setPending] = useState(null);
    const [focusModeVisible, setFocusModeVisible] = useState(false);
    const anchorRef = useRef({elapsed: 0, at: 0});
    const presenceAnchorsRef = useRef(new Map());
    const requestRef = useRef(0);
    const refreshPromiseRef = useRef(null);
    const pendingVisibleRefreshRef = useRef(false);
    const lastRealtimeSequenceRef = useRef(0);
    const lastRealtimeEventRef = useRef(null);
    const mutationRef = useRef(false);
    const openFocusMode = useCallback(() => setFocusModeVisible(true), []);
    const closeFocusMode = useCallback(() => setFocusModeVisible(false), []);

    const applySession = useCallback((nextSession) => {
        const active = nextSession && ["running", "paused"].includes(nextSession.focusSessionStatus)
            ? nextSession
            : null;
        if (!active) setFocusModeVisible(false);
        anchorRef.current = {
            elapsed: Math.max(0, Number(nextSession?.accumulatedSeconds || 0)),
            at: window.performance.now(),
        };
        setSession(active);
        return nextSession;
    }, []);

    const applyActiveSessions = useCallback((sessions) => {
        const presences = (Array.isArray(sessions) ? sessions : [])
            .filter((item) => ["running", "paused"].includes(item?.focusSessionStatus) && item?.focusedBy);
        const active = presences.filter((item) => item?.task);
        const now = window.performance.now();
        presenceAnchorsRef.current = new Map(active.map((item) => [String(entityIdentity(item.task)), {
            elapsed: Math.max(0, Number(item.accumulatedSeconds || 0)),
            at: now,
        }]));
        setFocusPresences(presences);
        setActiveSessions(active);
        return active;
    }, []);

    const applyRealtimeSession = useCallback((nextSession) => {
        if (!nextSession) return;
        const isActive = ["running", "paused"].includes(nextSession.focusSessionStatus);
        const now = window.performance.now();
        const taskKey = entityIdentity(nextSession.task);

        if (isActive && taskKey !== null && taskKey !== undefined) {
            presenceAnchorsRef.current.set(String(taskKey), {
                elapsed: Math.max(0, Number(nextSession.accumulatedSeconds || 0)),
                at: now,
            });
        } else if (taskKey !== null && taskKey !== undefined) {
            presenceAnchorsRef.current.delete(String(taskKey));
        }

        setFocusPresences((current) => mergeFocusSessionCollection(
            current,
            nextSession,
            (item) => Boolean(item?.focusedBy),
        ));
        setActiveSessions((current) => mergeFocusSessionCollection(
            current,
            nextSession,
            (item) => Boolean(item?.task),
        ));
    }, []);

    const refresh = useCallback(async () => {
        const refreshKey = `${workspaceId ?? ""}:${membership?.documentId ?? ""}`;
        if (refreshPromiseRef.current?.key === refreshKey) {
            return refreshPromiseRef.current.promise;
        }
        const requestId = ++requestRef.current;
        if (!workspaceId || !membership?.documentId) {
            applySession(null);
            applyActiveSessions([]);
            return null;
        }
        const promise = (async () => {
            setLoading(true);
            try {
                const [current, presences] = await Promise.all([
                    home.getCurrentFocusSession(workspaceId),
                    home.getActiveFocusSessions(workspaceId),
                ]);
                if (requestId === requestRef.current) {
                    applySession(current);
                    applyActiveSessions(presences);
                }
                return current;
            } finally {
                if (requestId === requestRef.current) setLoading(false);
            }
        })();
        refreshPromiseRef.current = {key: refreshKey, promise};
        promise.finally(() => {
            if (refreshPromiseRef.current?.promise === promise) refreshPromiseRef.current = null;
        }).catch(() => {});
        return promise;
    }, [applyActiveSessions, applySession, home, membership?.documentId, workspaceId]);

    useEffect(() => { refresh().catch(() => applySession(null)); }, [applySession, refresh]);

    useEffect(() => {
        const focusEventTypes = ["focus-session.updated", "focus-session.presence"];
        const queuedEvents = (Array.isArray(realtime.eventQueue) ? realtime.eventQueue : [])
            .filter((event) => (
                focusEventTypes.includes(event?.type)
                && Number(event?.__realtimeSequence || 0) > lastRealtimeSequenceRef.current
            ))
            .sort((left, right) => Number(left.__realtimeSequence) - Number(right.__realtimeSequence));
        const events = queuedEvents.length
            ? queuedEvents
            : (
                focusEventTypes.includes(realtime.lastEvent?.type)
                && !realtime.lastEvent?.__realtimeSequence
                && lastRealtimeEventRef.current !== realtime.lastEvent
                    ? [realtime.lastEvent]
                    : []
            );

        events.forEach((event) => {
            const sequence = Number(event?.__realtimeSequence || 0);
            if (sequence) lastRealtimeSequenceRef.current = Math.max(lastRealtimeSequenceRef.current, sequence);
            else lastRealtimeEventRef.current = event;

            const eventWorkspace = event.workspaceId ?? event.__realtimeWorkspaceId;
            // Socket events use the numeric workspace id while API routes often
            // use documentId. Compare against the complete workspace relation so
            // either identity is accepted.
            if (eventWorkspace && !sameEntity(eventWorkspace, membership?.workspace)) return;

            const nextSession = focusSessionFromRealtimeEvent(event);
            if (nextSession) {
                if (
                    event.type === "focus-session.updated"
                    && focusSessionBelongsToMembership(nextSession, membership)
                ) {
                    applySession(nextSession);
                }
                applyRealtimeSession(nextSession);
                return;
            }

            // Old realtime servers may not include the session payload. Defer
            // compatibility refreshes while hidden and collapse overlaps.
            if (document.visibilityState === "hidden") {
                pendingVisibleRefreshRef.current = true;
                return;
            }
            refresh().catch(() => {});
        });
    }, [applyRealtimeSession, applySession, membership, realtime.eventQueue, realtime.lastEvent, refresh]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState !== "visible" || !pendingVisibleRefreshRef.current) return;
            pendingVisibleRefreshRef.current = false;
            refresh().catch(() => {});
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    }, [refresh]);

    const getOwnElapsedSeconds = useCallback((now = window.performance.now()) => {
        const base = anchorRef.current.elapsed;
        if (session?.focusSessionStatus !== "running") return base;
        return base + Math.max(0, Math.floor((now - anchorRef.current.at) / 1000));
    }, [session?.focusSessionStatus]);

    const run = useCallback(async (key, action) => {
        if (mutationRef.current) return null;
        mutationRef.current = true;
        setPending(key);
        try {
            const nextSession = await action();
            const nextIsActive = ["running", "paused"].includes(nextSession?.focusSessionStatus);
            if (nextSession && !nextIsActive) {
                const taskKey = entityIdentity(nextSession.task);
                if (taskKey !== null && taskKey !== undefined) {
                    presenceAnchorsRef.current.delete(String(taskKey));
                    setActiveSessions((current) => current.filter((item) => !sameEntity(item.task, nextSession.task)));
                }
            }
            applyRealtimeSession(nextSession);
            return applySession(nextSession);
        } catch (error) {
            if (["FOCUS_SESSION_VERSION_CONFLICT", "FOCUS_SESSION_NOT_ACTIVE", "FOCUS_SESSION_ACTIVE"].includes(error?.code)) {
                await refresh().catch(() => {});
            }
            throw error;
        } finally {
            mutationRef.current = false;
            setPending(null);
        }
    }, [applyRealtimeSession, applySession, refresh]);

    const start = useCallback((task, targetDurationSeconds = null) => run("start", () => (
        home.startFocusSession(workspaceId, entityIdentity(task), targetDurationSeconds)
    )), [home, run, workspaceId]);

    const mutate = useCallback((action, data = {}) => {
        const sessionId = entityIdentity(session);
        if (!sessionId) return Promise.reject(new Error("focus-session-missing"));
        return run(action, () => home.mutateFocusSession(workspaceId, sessionId, action, session.version, data));
    }, [home, run, session, workspaceId]);

    const isTaskActive = useCallback((task) => Boolean(session && sameEntity(session.task, task)), [session]);
    const getTaskPresence = useCallback((task) => activeSessions.find((item) => sameEntity(item.task, task)) || null, [activeSessions]);
    const getTaskElapsedSeconds = useCallback((task, now = window.performance.now()) => {
        const presence = getTaskPresence(task);
        if (!presence) return 0;
        const anchor = presenceAnchorsRef.current.get(String(entityIdentity(presence.task))) || {elapsed: Number(presence.accumulatedSeconds || 0), at: now};
        if (presence.focusSessionStatus !== "running") return anchor.elapsed;
        return anchor.elapsed + Math.max(0, Math.floor((now - anchor.at) / 1000));
    }, [getTaskPresence]);
    const isAssignee = useCallback((task) => sameEntity(task?.assignee, membership), [membership]);
    const getMemberFocusStatus = useCallback((memberData) => {
        const ownStatus = session && memberFocusStatus(memberData, [{focusSessionStatus: session.focusSessionStatus, focusedBy: membership}]);
        return ownStatus || memberFocusStatus(memberData, focusPresences);
    }, [focusPresences, membership, session]);
    const isMemberFocusing = useCallback((memberData) => getMemberFocusStatus(memberData) === "running", [getMemberFocusStatus]);

    const value = useMemo(() => ({
        session,
        activeSessions,
        getOwnElapsedSeconds,
        loading,
        pending,
        focusModeVisible,
        openFocusMode,
        closeFocusMode,
        refresh,
        start,
        mutate,
        isTaskActive,
        getTaskPresence,
        getTaskElapsedSeconds,
        isAssignee,
        getMemberFocusStatus,
        isMemberFocusing,
        isTaskLocked: (task) => isTaskActive(task) && isAssignee(task),
    }), [activeSessions, closeFocusMode, focusModeVisible, getMemberFocusStatus, getOwnElapsedSeconds, getTaskElapsedSeconds, getTaskPresence, isAssignee, isMemberFocusing, isTaskActive, loading, mutate, openFocusMode, pending, refresh, session, start]);

    return <FocusSessionContext.Provider value={value}>{children}</FocusSessionContext.Provider>;
}

export function useFocusSession() {
    const context = useContext(FocusSessionContext);
    if (!context) throw new Error("useFocusSession must be used inside FocusSessionProvider");
    return context;
}

export function useOptionalFocusSession() {
    return useContext(FocusSessionContext);
}
